"use client"

import * as React from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  CalendarDaysIcon,
  ChurchIcon,
  CircleCheckIcon,
  GitBranchIcon,
  HandCoinsIcon,
  HandHeartIcon,
  MinusIcon,
  PiggyBankIcon,
  PlusIcon,
  PrinterIcon,
  TagIcon,
  UsersIcon,
  type LucideIcon,
} from "lucide-react"
import { useForm, useWatch, type Control } from "react-hook-form"
import { toast } from "sonner"

import { Button, Form, FormControlledField, Input, Switch, Textarea, cn } from "@ibada/ui"

import { ReceiptPaperFit } from "@/features/finance/components/receipts/receipt-paper-preview"
import { ReceiptSignatureDialog } from "@/features/finance/components/receipts/receipt-signature-dialog"
import {
  isDetailFieldEnabled,
  normalizeReceiptConfig,
  populateReceiptConfigDefaults,
  toggleDetailField,
} from "@/features/finance/lib/receipt-config"
import {
  RECEIPT_DETAIL_FIELDS,
  RECEIPT_TEMPLATE_KEYS,
  ReceiptDetailField,
  ReceiptTemplateKey,
  type ReceiptConfig,
  type ReceiptConfigDefaults,
} from "@/features/finance/model/finance-types"
import {
  receiptSettingsFormSchema,
  toReceiptConfig,
  toReceiptSettingsFormValues,
  type ReceiptSettingsFormValues,
} from "@/features/finance/schemas/receipt-settings-form"
import {
  usePreviewReceiptConfigMutation,
  useReceiptConfigQuery,
  useUpdateReceiptConfigMutation,
} from "@/features/finance/services/finance-query"
import { useTranslations } from "@/i18n"
import { AlivePillTone, AliveStatusCell } from "@/shared/components/alive-cells"
import { InlineSettingsRow } from "@/shared/components/inline-settings/inline-settings"
import { LoadingRegion, SkeletonFormCard } from "@/shared/components/skeletons"
import { useApiErrorToast } from "@/shared/hooks/use-api-error"

const PREVIEW_DEBOUNCE_MS = 180
const ZOOM_STEP = 0.1
const ZOOM_MIN = 0.25
const ZOOM_MAX = 2

const DETAIL_FIELD_ICONS: Record<ReceiptDetailField, LucideIcon> = {
  [ReceiptDetailField.Pledge]: HandCoinsIcon,
  [ReceiptDetailField.Activity]: CalendarDaysIcon,
  [ReceiptDetailField.Category]: TagIcon,
  [ReceiptDetailField.Fund]: PiggyBankIcon,
  [ReceiptDetailField.Group]: UsersIcon,
  [ReceiptDetailField.Ministry]: HandHeartIcon,
  [ReceiptDetailField.Workspace]: ChurchIcon,
  [ReceiptDetailField.Branch]: GitBranchIcon,
}

type ReceiptPreviews = Partial<Record<ReceiptTemplateKey, string>>

/**
 * Receipt settings, design R1: the settings on the left, the live receipt on the right.
 *
 * One RHF form holds every setting. The preview (and the three template miniatures) render from the form as it
 * is typed, before anything is saved; saving is explicit, from the bar that appears only when the form is
 * dirty. The endpoint takes the whole config, so Enregistrer sends all of it.
 */
export function ReceiptSettingsEditor({
  workspaceId,
  branchId,
  canEdit,
  showHeader,
}: {
  workspaceId: string
  branchId: string
  canEdit: boolean
  showHeader: boolean
}) {
  const t = useTranslations("finance.receipts")
  const { data, isLoading } = useReceiptConfigQuery({ workspaceId, branchId })
  const [loaded, setLoaded] = React.useState<{ values: ReceiptSettingsFormValues; defaults: ReceiptConfigDefaults } | null>(null)

  React.useEffect(() => {
    if (!data) return
    const config = populateReceiptConfigDefaults(normalizeReceiptConfig(data.config), data.defaults, {
      signatureLabel: t("placeholders.signatureLabel"),
      thankYou: t("placeholders.notesText"),
    })
    setLoaded({ values: toReceiptSettingsFormValues(config), defaults: data.defaults })
    // `t` excluded: a new identity is not new data, and re-running would throw away what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  if (isLoading || !loaded) {
    return (
      <LoadingRegion label={t("loading")}>
        <SkeletonFormCard fields={4} />
      </LoadingRegion>
    )
  }

  return (
    <ReceiptSettingsForm
      workspaceId={workspaceId}
      branchId={branchId}
      canEdit={canEdit}
      showHeader={showHeader}
      initialValues={loaded.values}
      defaults={loaded.defaults}
    />
  )
}

function ReceiptSettingsForm({
  workspaceId,
  branchId,
  canEdit,
  showHeader,
  initialValues,
  defaults,
}: {
  workspaceId: string
  branchId: string
  canEdit: boolean
  showHeader: boolean
  initialValues: ReceiptSettingsFormValues
  defaults: ReceiptConfigDefaults
}) {
  const t = useTranslations("finance.receipts")
  const toastError = useApiErrorToast("finance")
  const { mutateAsync: updateConfig } = useUpdateReceiptConfigMutation()
  const [signatureOpen, setSignatureOpen] = React.useState(false)

  const form = useForm<ReceiptSettingsFormValues>({
    resolver: zodResolver(receiptSettingsFormSchema),
    defaultValues: initialValues,
  })

  // A refetch with new saved values (after a save, or from elsewhere) becomes the new clean state.
  React.useEffect(() => {
    if (!form.formState.isDirty) form.reset(initialValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues])

  const values = useWatch({ control: form.control }) as ReceiptSettingsFormValues
  const liveConfig = React.useMemo(() => toReceiptConfig({ ...initialValues, ...values }), [initialValues, values])
  const previews = useReceiptTemplatePreviews({ workspaceId, branchId, config: liveConfig })

  const isDirty = form.formState.isDirty
  const isSaving = form.formState.isSubmitting

  const submit = form.handleSubmit(async (submitted) => {
    if (!canEdit) return
    try {
      await updateConfig({ workspaceId, branchId, config: toReceiptConfig(submitted) })
      form.reset(submitted)
      toast.success(t("toasts.saved"))
    } catch (error) {
      toastError(error)
    }
  })

  const setValue = <TKey extends keyof ReceiptSettingsFormValues>(key: TKey, value: ReceiptSettingsFormValues[TKey]) =>
    form.setValue(key, value as never, { shouldDirty: true })

  return (
    <Form {...form}>
      <form onSubmit={(event) => void submit(event)} className='flex flex-col gap-5'>
        {/* The title row doubles as the save bar: it sticks, and the actions only exist while there is something to save. */}
        <div
          className={cn(
            "flex flex-wrap items-center justify-between gap-3 py-2",
            isDirty && canEdit && "bg-card shadow-app-popover sticky top-0 z-20 rounded-2xl border px-4",
          )}
        >
          {showHeader ? (
            <div className='min-w-0 space-y-1'>
              <h2 className='text-2xl font-semibold tracking-tight'>{t("title")}</h2>
              <p className='text-muted-foreground text-sm'>{t("description")}</p>
            </div>
          ) : (
            <span />
          )}
          {isDirty && canEdit ? (
            <div className='flex items-center gap-2' role='status'>
              <span className='text-muted-foreground text-sm font-semibold'>{t("unsaved")}</span>
              <Button type='button' variant='outline' size='sm' className='rounded-full' onClick={() => form.reset()} disabled={isSaving}>
                {t("actions.cancel")}
              </Button>
              <Button type='submit' size='sm' className='rounded-full' loading={isSaving}>
                {t("actions.save")}
              </Button>
            </div>
          ) : null}
        </div>

        <div className='grid items-start gap-5 lg:grid-cols-[470px_minmax(0,1fr)]'>
          <div className='flex min-w-0 flex-col gap-5'>
            <TemplatePicker
              selected={values.templateKey ?? initialValues.templateKey}
              previews={previews}
              canEdit={canEdit}
              onSelect={(templateKey) => setValue("templateKey", templateKey)}
            />

            <ReceiptTextFields
              control={form.control}
              defaults={defaults}
              canEdit={canEdit}
              signatureSvg={values.signatureSvg ?? null}
              onOpenSignature={() => setSignatureOpen(true)}
              onRemoveSignature={() => setValue("signatureSvg", null)}
            />

            <DetailSwitches
              config={liveConfig}
              canEdit={canEdit}
              onToggle={(field, enabled) => setValue("detailFields", toggleDetailField(liveConfig, field, enabled))}
            />
          </div>

          <ReceiptPreviewCard
            html={previews[liveConfig.templateKey] ?? ""}
            templateKey={liveConfig.templateKey}
          />
        </div>

        <ReceiptSignatureDialog
          open={signatureOpen}
          signatureSvg={values.signatureSvg ?? null}
          canEdit={canEdit}
          onOpenChange={setSignatureOpen}
          onSignatureChange={(signatureSvg) => setValue("signatureSvg", signatureSvg)}
        />
      </form>
    </Form>
  )
}

/** One render per template from the live config, refreshed (debounced) whenever the config changes. */
function useReceiptTemplatePreviews({
  workspaceId,
  branchId,
  config,
}: {
  workspaceId: string
  branchId: string
  config: ReceiptConfig
}): ReceiptPreviews {
  const t = useTranslations("finance.receipts")
  const { mutateAsync: previewReceipt } = usePreviewReceiptConfigMutation()
  const [previews, setPreviews] = React.useState<ReceiptPreviews>({})
  const requestIdRef = React.useRef(0)
  // Watched values are a fresh object on every keystroke; the serialized config is what actually changed.
  const configKey = JSON.stringify(config)

  React.useEffect(() => {
    const current = JSON.parse(configKey) as ReceiptConfig
    requestIdRef.current += 1
    const requestId = requestIdRef.current
    const handle = window.setTimeout(() => {
      Promise.all(
        RECEIPT_TEMPLATE_KEYS.map((templateKey) =>
          previewReceipt({ workspaceId, branchId, config: { ...current, templateKey } }).then(
            (result) => [templateKey, result.html] as const,
          ),
        ),
      )
        .then((entries) => {
          if (requestId === requestIdRef.current) setPreviews(Object.fromEntries(entries))
        })
        .catch(() => {
          if (requestId === requestIdRef.current) toast.error(t("toasts.previewError"))
        })
    }, PREVIEW_DEBOUNCE_MS)
    return () => window.clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, configKey, previewReceipt, workspaceId])

  return previews
}

function SectionTitle({ children, hint }: { children: React.ReactNode; hint?: React.ReactNode }) {
  return (
    <div className='pl-1'>
      <h3 className='text-sm font-bold'>{children}</h3>
      {hint ? <p className='text-muted-foreground text-[12.5px]'>{hint}</p> : null}
    </div>
  )
}

function TemplatePicker({
  selected,
  previews,
  canEdit,
  onSelect,
}: {
  selected: ReceiptTemplateKey
  previews: ReceiptPreviews
  canEdit: boolean
  onSelect: (templateKey: ReceiptTemplateKey) => void
}) {
  const t = useTranslations("finance.receipts")

  return (
    <section className='flex flex-col gap-2'>
      <SectionTitle>{t("fields.templateKey")}</SectionTitle>
      <div className='grid grid-cols-3 gap-2.5' role='radiogroup' aria-label={t("fields.templateKey")}>
        {RECEIPT_TEMPLATE_KEYS.map((key) => {
          const isSelected = key === selected
          const name = t(`templates.${key}.name` as never)
          return (
            <button
              key={key}
              type='button'
              role='radio'
              aria-checked={isSelected}
              aria-label={name}
              data-testid='receipt-template-card'
              data-template={key}
              disabled={!canEdit}
              onClick={() => onSelect(key)}
              className={cn(
                "bg-card relative flex flex-col overflow-hidden rounded-2xl border text-left transition-colors disabled:cursor-not-allowed",
                isSelected ? "border-primary ring-primary ring-1" : "hover:border-muted-foreground/40",
              )}
            >
              {isSelected ? (
                <CircleCheckIcon className='text-primary bg-card absolute top-2 right-2 z-10 size-5 rounded-full' aria-hidden />
              ) : null}
              <ReceiptPaperFit
                html={previews[key] ?? ""}
                templateKey={key}
                title={t("templates.previewOf", { name })}
                padding={8}
                className='h-[120px]'
              />
              <span className='flex flex-col gap-0.5 border-t px-2.5 py-2'>
                <span className='text-[13px] font-bold'>
                  {name} <span className='text-muted-foreground font-semibold'>{t(`templates.${key}.paper` as never)}</span>
                </span>
                <span className='text-muted-foreground line-clamp-2 text-[11.5px] leading-snug'>{t(`templates.${key}.description` as never)}</span>
              </span>
            </button>
          )
        })}
      </div>
    </section>
  )
}

function ReceiptTextFields({
  control,
  defaults,
  canEdit,
  signatureSvg,
  onOpenSignature,
  onRemoveSignature,
}: {
  control: Control<ReceiptSettingsFormValues>
  defaults: ReceiptConfigDefaults
  canEdit: boolean
  signatureSvg: string | null
  onOpenSignature: () => void
  onRemoveSignature: () => void
}) {
  const t = useTranslations("finance.receipts")
  type TextKey = Exclude<keyof ReceiptSettingsFormValues, "templateKey" | "detailFields" | "signatureSvg">

  const field = (name: TextKey, placeholder: string, multiline = false) => (
    <FormControlledField
      control={control}
      name={name}
      label={t(`fields.${name}` as never)}
      renderInput={({ field: input }) =>
        multiline ? (
          <Textarea {...input} value={input.value ?? ""} rows={3} placeholder={placeholder} disabled={!canEdit} />
        ) : (
          <Input {...input} value={input.value ?? ""} placeholder={placeholder} disabled={!canEdit} />
        )
      }
    />
  )

  return (
    <section className='flex flex-col gap-2'>
      <SectionTitle>{t("sections.text")}</SectionTitle>
      <div className='bg-card flex flex-col gap-4 rounded-2xl border p-4'>
        {field("organizationNameOverride", defaults.organizationName || t("placeholders.organizationNameOverride"))}
        {field("branchNameOverride", defaults.branchName || t("placeholders.branchNameOverride"))}
        {field("headerSummary", defaults.headerSummary || t("placeholders.headerSummary"), true)}
        {field("notesText", t("placeholders.notesText"), true)}
        {field("footerText", t("placeholders.footerText"), true)}
        {field("footerContacts", defaults.footerContacts || t("placeholders.footerContacts"), true)}
      </div>

      <SectionTitle>{t("fields.signature")}</SectionTitle>
      <div className='bg-card flex flex-col gap-4 rounded-2xl border p-4'>
        {field("signatureLabel", t("placeholders.signatureLabel"))}
        {field("signedByName", t("placeholders.signedByName"))}
        {field("signedByTitle", t("placeholders.signedByTitle"))}
        <div className='flex flex-wrap items-center gap-2'>
          {signatureSvg ? (
            <span className='inline-flex rounded-lg bg-white px-2 py-1 ring-1 ring-border/60'>
              <img src={signatureSvg} alt={t("fields.signature")} className='h-10 max-w-[10rem] object-contain' />
            </span>
          ) : (
            <span className='text-muted-foreground text-sm'>{t("placeholders.signature")}</span>
          )}
          <span className='ml-auto flex flex-wrap items-center gap-2'>
            {signatureSvg ? (
              <Button type='button' variant='destructive' size='sm' className='rounded-full' disabled={!canEdit} onClick={onRemoveSignature}>
                {t("actions.removeSignature")}
              </Button>
            ) : null}
            <Button type='button' variant='outline' size='sm' className='rounded-full' disabled={!canEdit} onClick={onOpenSignature}>
              {signatureSvg ? t("actions.editSignature") : t("actions.addSignature")}
            </Button>
          </span>
        </div>
      </div>
    </section>
  )
}

function DetailSwitches({
  config,
  canEdit,
  onToggle,
}: {
  config: ReceiptConfig
  canEdit: boolean
  onToggle: (field: ReceiptDetailField, enabled: boolean) => void
}) {
  const t = useTranslations("finance.receipts")

  return (
    <section className='flex flex-col gap-2'>
      <SectionTitle hint={t("hints.detailFields")}>{t("fields.detailFields")}</SectionTitle>
      <div className='bg-card divide-border/60 divide-y overflow-hidden rounded-2xl border'>
        {RECEIPT_DETAIL_FIELDS.map((field) => {
          const label = t(`detailFields.${field}` as never)
          return (
            <InlineSettingsRow
              key={field}
              icon={DETAIL_FIELD_ICONS[field]}
              label={label}
              hint={t(`detailFieldHints.${field}` as never)}
              value={
                <Switch
                  checked={isDetailFieldEnabled(config, field)}
                  onCheckedChange={(checked) => onToggle(field, checked)}
                  disabled={!canEdit}
                  aria-label={label}
                  className='align-middle'
                />
              }
            />
          )
        })}
      </div>
    </section>
  )
}

/** Zoom: fit the whole page (default), or a fixed scale set with 100 % and the minus / plus buttons. */
export enum ReceiptPreviewZoomMode {
  Fit = "fit",
  Fixed = "fixed",
}

function ReceiptPreviewCard({ html, templateKey }: { html: string; templateKey: ReceiptTemplateKey }) {
  const t = useTranslations("finance.receipts")
  const frameRef = React.useRef<HTMLIFrameElement | null>(null)
  const [mode, setMode] = React.useState(ReceiptPreviewZoomMode.Fit)
  const [zoom, setZoom] = React.useState(1)
  const fitScaleRef = React.useRef(1)
  const onScaleChange = React.useCallback(
    (scale: number) => {
      if (mode === ReceiptPreviewZoomMode.Fit) fitScaleRef.current = scale
    },
    [mode],
  )

  const step = (direction: 1 | -1) => {
    const from = mode === ReceiptPreviewZoomMode.Fit ? fitScaleRef.current : zoom
    const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((from + direction * ZOOM_STEP) * 10) / 10))
    setMode(ReceiptPreviewZoomMode.Fixed)
    setZoom(next)
  }
  const isFit = mode === ReceiptPreviewZoomMode.Fit
  const boxRef = React.useRef<HTMLDivElement | null>(null)
  const visibleHeight = useVisibleHeight(boxRef)
  const zoomLabel = t("zoom.percent", { value: Math.round(zoom * 100) })

  return (
    <section className='bg-card flex min-w-0 flex-col overflow-hidden rounded-2xl border lg:sticky lg:top-28'>
      <div className='flex flex-wrap items-center gap-2 border-b px-4 py-3'>
        <h3 className='text-sm font-bold'>{t("preview.heading")}</h3>
        <AliveStatusCell tone={AlivePillTone.Neutral}>{t("preview.sample")}</AliveStatusCell>
        <div className='ml-auto flex items-center gap-2'>
          <div className='bg-muted flex items-center gap-0.5 rounded-full p-0.5' role='group' aria-label={t("zoom.label")}>
            <Button type='button' variant='ghost' size='icon-sm' className='rounded-full' aria-label={t("zoom.out")} onClick={() => step(-1)}>
              <MinusIcon aria-hidden />
            </Button>
            <Button
              type='button'
              variant={isFit ? "outline" : "ghost"}
              size='sm'
              className='rounded-full'
              aria-pressed={isFit}
              onClick={() => setMode(ReceiptPreviewZoomMode.Fit)}
            >
              {t("zoom.fit")}
            </Button>
            <Button
              type='button'
              variant={!isFit ? "outline" : "ghost"}
              size='sm'
              className='rounded-full tabular-nums'
              aria-pressed={!isFit}
              onClick={() => {
                setMode(ReceiptPreviewZoomMode.Fixed)
                setZoom(1)
              }}
            >
              {isFit ? t("zoom.percent", { value: 100 }) : zoomLabel}
            </Button>
            <Button type='button' variant='ghost' size='icon-sm' className='rounded-full' aria-label={t("zoom.in")} onClick={() => step(1)}>
              <PlusIcon aria-hidden />
            </Button>
          </div>
          <Button
            type='button'
            variant='outline'
            size='sm'
            className='rounded-full'
            disabled={!html}
            onClick={() => frameRef.current?.contentWindow?.print()}
          >
            <PrinterIcon aria-hidden />
            {t("actions.printTest")}
          </Button>
        </div>
      </div>
      <div ref={boxRef} style={visibleHeight ? { height: visibleHeight } : undefined} className='flex min-h-[360px] flex-col'>
      <ReceiptPaperFit
        html={html}
        templateKey={templateKey}
        title={t("preview.title")}
        zoom={isFit ? null : zoom}
        placeholder={t("preview.loading")}
        frameRef={frameRef}
        onScaleChange={onScaleChange}
        className='flex-1'
      />
      </div>
    </section>
  )
}

/**
 * The height from the element's top to the bottom of the window (less a margin), kept current while the page
 * scrolls and the window resizes. The preview box uses it so the whole page fits what is actually visible:
 * shorter before the card has stuck, taller once it has.
 */
function useVisibleHeight(ref: React.RefObject<HTMLElement | null>, bottomMargin = 16): number | null {
  const [height, setHeight] = React.useState<number | null>(null)
  React.useEffect(() => {
    let frame = 0
    const update = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const element = ref.current
        if (!element) return
        const next = Math.round(window.innerHeight - element.getBoundingClientRect().top - bottomMargin)
        setHeight((prev) => (prev === next ? prev : next))
      })
    }
    update()
    // Capture: the page scrolls inside the app shell, not the window, and scroll events do not bubble.
    window.addEventListener("scroll", update, true)
    window.addEventListener("resize", update)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener("scroll", update, true)
      window.removeEventListener("resize", update)
    }
  }, [bottomMargin, ref])
  return height
}
